import { getLogger } from '@logtape/logtape';
import { useState } from 'react';

import { CHAIN_INTERACTION_KEY } from '@/constants/availability-telemetry.ts';

import { settleAvailabilityInteraction } from '@/lib/availability-telemetry.ts';

import type {
    ChainAutoSuggestionParams,
    ChainAvailabilityItem,
    ChainAvailabilityParams,
} from '@/models/appointment/AppointmentChainAvailability.ts';

import { getChainAutoSuggestion, getChainAvailability } from '@/services/availabilityService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

import { toAvailabilityFailureReason } from '@/utils/availability-utils.ts';

const logger = getLogger(['hook', 'chain-availability']);

const GENERIC_AVAILABILITY_ERROR_MESSAGE = 'Verfügbarkeit konnte nicht geladen werden.';

const settleChainFailure = (error: unknown): void => {
    settleAvailabilityInteraction({
        key: CHAIN_INTERACTION_KEY,
        outcome: 'failure',
        reason: toAvailabilityFailureReason(error),
    });
};

interface UseChainAvailabilityResult {
    fetchAvailability: (params: ChainAvailabilityParams) => Promise<ChainAvailabilityItem[] | undefined>;
    fetchAutoSuggestion: (params: ChainAutoSuggestionParams) => Promise<ChainAvailabilityItem[] | undefined>;
    isFetching: boolean;
}

export const useChainAvailability = (): UseChainAvailabilityResult => {
    const [isFetching, setIsFetching] = useState(false);

    const fetchAvailability = async (params: ChainAvailabilityParams): Promise<ChainAvailabilityItem[] | undefined> => {
        setIsFetching(true);
        let items: ChainAvailabilityItem[] | undefined;
        try {
            items = await getChainAvailability(params);
        } catch (error) {
            logger.error('Chain availability fetch failed', { error: error });
            setIsFetching(false);
            showErrorNotification(GENERIC_AVAILABILITY_ERROR_MESSAGE);
            settleChainFailure(error);
            return undefined;
        }
        setIsFetching(false);
        return items;
    };

    const fetchAutoSuggestion = async (
        params: ChainAutoSuggestionParams
    ): Promise<ChainAvailabilityItem[] | undefined> => {
        setIsFetching(true);
        let items: ChainAvailabilityItem[] | undefined;
        try {
            items = await getChainAutoSuggestion(params);
        } catch (error) {
            logger.error('Chain auto-suggestion fetch failed', { error: error });
            setIsFetching(false);
            showErrorNotification(GENERIC_AVAILABILITY_ERROR_MESSAGE);
            settleChainFailure(error);
            return undefined;
        }
        setIsFetching(false);
        return items;
    };

    return { fetchAvailability, fetchAutoSuggestion, isFetching };
};
